import {
	type GeoFailureReason,
	GEO_FAILURE_REASON,
} from './geo-failure-reason'
import {
	coerceToGeoLocationError,
	reasonFromGeoLocationError,
} from './geo-location-errors'

const GEOLOCATION_PERMISSION_NAME = 'geolocation' as PermissionName
const PERMISSION_STATE_DENIED = 'denied'
const PERMISSION_STATE_GRANTED = 'granted'
const PERMISSION_STATE_PROMPT = 'prompt'

export function inferReasonFromError(err: unknown): GeoFailureReason | undefined {
	const normalized = coerceToGeoLocationError(err)
	return reasonFromGeoLocationError(normalized)
}

/**
 * When the SDK returns Failed, infer reason using the Permissions API.
 * - permission_denied: geolocation permission is 'denied' or 'prompt'
 * - verification_failed: permission is 'granted' but SDK failed (VPN/integrity)
 *
 * Limitations of the Permissions API for geolocation (tested 2026-03-17):
 * - Prompt-based deny: ALL browsers report 'prompt', not 'denied'. This means a user who
 *   clicks "Block"/"Don't Allow" on the one-time prompt will NOT be detected via 'denied'.
 *   We treat 'prompt' as permission_denied because this function only runs after the SDK
 *   has already failed -- if the user had allowed, the state would be 'granted'.
 * - Persistent block (site settings): Chrome and Firefox report 'denied' (correct).
 *   Safari never reports 'denied' regardless of how the permission was blocked.
 * - Admin-managed Chrome may also report 'prompt' after explicit deny.
 */
export async function inferReasonFromPermissions(): Promise<GeoFailureReason | undefined> {
	try {
		if (!navigator.permissions?.query) return undefined
		const permissionStatus = await navigator.permissions.query({
			name: GEOLOCATION_PERMISSION_NAME,
		})
		if (permissionStatus.state === PERMISSION_STATE_DENIED)
			return GEO_FAILURE_REASON.PERMISSION_DENIED
		if (permissionStatus.state === PERMISSION_STATE_GRANTED)
			return GEO_FAILURE_REASON.VERIFICATION_FAILED
		if (permissionStatus.state === PERMISSION_STATE_PROMPT)
			return GEO_FAILURE_REASON.PERMISSION_DENIED
	} catch {
		// Permissions API not supported or threw
	}
	return undefined
}
